import { RefObject, useEffect, useState } from 'react';

/**
 * Given a ref to a scrollable container of elements, returns the elements that are not completely visible and
 * are off view because of scroll. It relies on data-key property of observed elements.
 * @param scrollableItemsContainerRef
 * @param threshold
 * @param horizontalOffset
 */
export function useOverflowObserver(
  scrollableItemsContainerRef: RefObject<HTMLElement>,
  {
    threshold = 1,
    horizontalOffset = 0,
  }: { threshold?: number; horizontalOffset?: number } = {}
): {
  intersectionObserver: IntersectionObserver | null;
  overflowedElements: Set<HTMLElement>;
} {
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const [overflowedElements, setOverflowedElements] = useState<
    Set<HTMLElement>
  >(new Set());

  useEffect(() => {
    const notScrollable =
      scrollableItemsContainerRef.current?.scrollWidth ===
      scrollableItemsContainerRef.current?.offsetWidth;
    if (notScrollable && overflowedElements.size > 0) {
      setOverflowedElements(new Set());
    }
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const newHiddenElements = entries
          .filter((entry) => !entry.isIntersecting)
          .map((entry) => entry.target)
          .filter((val): val is HTMLElement => Boolean(val));

        const newVisibleElements = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target)
          .filter((val): val is HTMLElement => Boolean(val));

        setOverflowedElements(
          (currentOverflowedElements) =>
            new Set(
              Array.from(currentOverflowedElements)
                .filter((element) => !newVisibleElements.includes(element))
                .concat(newHiddenElements)
            )
        );
      },
      {
        root: scrollableItemsContainerRef.current,
        rootMargin: `0px -${horizontalOffset}px 0px -${horizontalOffset}px`,
        threshold,
      }
    );
    setIntersectionObserver(observer);
    return () => {
      observer.disconnect();
    };
  }, [horizontalOffset]);

  return {
    intersectionObserver,
    overflowedElements,
  };
}
